import { useState, useEffect } from 'react';
import { useContent } from '../contexts/ContentContext';
import { FaLock, FaSave, FaUndo, FaEdit } from 'react-icons/fa';

interface ContentSection {
  [key: string]: string | { [key: string]: string };
}

interface Content {
  hero: {
    quote: string;
    description: string;
  };
  about: {
    title: string;
    description: string;
    formacao: string;
    experiencia: string;
    abordagem: string;
  };
  services: {
    title: string;
    description: string;
    [key: string]: string;
  };
  contact: {
    [key: string]: string;
  };
}

const defaultContent = {
  hero: {
    quote: 'A terapia é um espaço de autoconhecimento, crescimento e transformação.',
    description: 'Especialista em Terapia Cognitivo-Comportamental, oferecendo um espaço seguro e acolhedor para seu processo terapêutico.'
  },
  about: {
    title: 'Sobre Mim',
    description: 'Sou psicóloga clínica com formação em Terapia Cognitivo-Comportamental. Minha abordagem é centrada no cliente, buscando proporcionar um ambiente acolhedor e seguro para o processo terapêutico.',
    formacao: 'Psicóloga formada com especialização em Terapia Cognitivo-Comportamental',
    experiencia: 'Anos de prática clínica com atendimento humanizado e acolhedor',
    abordagem: 'Foco em resultados com técnicas baseadas em evidências científicas'
  },
  services: {
    title: 'Serviços',
    description: 'Atendimento psicológico personalizado e humanizado',
    service1Title: 'Psicoterapia Individual',
    service1Description: 'Atendimento personalizado para adultos, focado no autoconhecimento e desenvolvimento pessoal.',
    service2Title: 'Terapia Online',
    service2Description: 'Sessões realizadas por videoconferência, oferecendo flexibilidade e comodidade.',
    service3Title: 'Palestras',
    service3Description: 'Apresentações e workshops sobre temas relacionados à saúde mental, desenvolvimento pessoal e bem-estar emocional para empresas e grupos.'
  },
  contact: {
    title: 'Como Chegar',
    description: 'Entre em contato e agende sua consulta',
    address: 'Rua S-2, 913 - St. Bela Vista, Goiânia - GO',
    phone: '(62) 98149-0616',
    instagram: '@psi.ke',
    schedule: 'Segunda a Sexta, 08:00 - 18:00'
  }
};

const sections = [
  {
    id: 'hero',
    name: 'Hero',
    fields: ['quote', 'description']
  },
  {
    id: 'about',
    name: 'Sobre',
    fields: ['title', 'description', 'formacao', 'experiencia', 'abordagem']
  },
  {
    id: 'services',
    name: 'Serviços',
    fields: [
      'title', 'description',
      'service1Title', 'service1Description',
      'service2Title', 'service2Description',
      'service3Title', 'service3Description'
    ]
  },
  {
    id: 'contact',
    name: 'Contato',
    fields: ['title', 'description', 'address', 'phone', 'instagram', 'schedule']
  }
];

const AdminPanel = () => {
  const { content, updateSection } = useContent();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [editingSection, setEditingSection] = useState<string | null>(null);
  const [editedContent, setEditedContent] = useState<any>({});

  // Restaurar o conteúdo do Hero automaticamente quando o componente montar
  useEffect(() => {
    updateSection('hero', defaultContent.hero);
  }, []); // Executa apenas uma vez quando o componente monta

  const handleLogin = () => {
    if (password === 'dlink500b') {
      setIsAuthenticated(true);
      setError('');
      setPassword('');
    } else {
      setError('Senha incorreta');
    }
  };

  const startEditing = (sectionId: string) => {
    setEditingSection(sectionId);
    setEditedContent({ ...content[sectionId] });
  };

  const handleSave = (sectionId: string) => {
    updateSection(sectionId, editedContent);
    setEditingSection(null);
  };

  const restoreDefault = (sectionId: string) => {
    updateSection(sectionId, defaultContent[sectionId]);
    setEditingSection(null);
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#F8F3F0] flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-2xl shadow-lg max-w-md w-full">
          <h1 className="text-2xl font-bold text-[#4A3B36] mb-6 text-center">Painel Administrativo</h1>
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-[#6B5C57] mb-4">
              <FaLock />
              <span>Este painel é protegido por senha</span>
            </div>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
              placeholder="Digite a senha"
              className="w-full px-4 py-2 rounded-xl border border-[#D4B4A8] focus:outline-none focus:ring-2 focus:ring-[#4A3B36]"
            />
            {error && <p className="text-red-500 text-sm">{error}</p>}
            <button
              onClick={handleLogin}
              className="w-full bg-[#4A3B36] text-white py-2 rounded-xl hover:bg-[#6B5C57] transition-colors"
            >
              Entrar
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F3F0] p-6">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold text-[#4A3B36] mb-8">Painel Administrativo</h1>
        <div className="space-y-8">
          {sections.map((section) => (
            <div key={section.id} className="bg-white border border-[#D4B4A8] rounded-xl p-6 shadow-sm">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-semibold text-[#4A3B36]">{section.name}</h3>
                <div className="flex gap-2">
                  <button
                    onClick={() => restoreDefault(section.id)}
                    className="text-[#6B5C57] hover:text-[#4A3B36] bg-[#F8F3F0] p-2 rounded-lg"
                    title="Restaurar texto original"
                  >
                    <FaUndo className="text-xl" />
                  </button>
                  {editingSection === section.id ? (
                    <button
                      onClick={() => handleSave(section.id)}
                      className="text-[#6B5C57] hover:text-[#4A3B36] bg-[#F8F3F0] p-2 rounded-lg"
                    >
                      <FaSave className="text-xl" />
                    </button>
                  ) : (
                    <button
                      onClick={() => startEditing(section.id)}
                      className="text-[#6B5C57] hover:text-[#4A3B36] bg-[#F8F3F0] p-2 rounded-lg"
                    >
                      <FaEdit className="text-xl" />
                    </button>
                  )}
                </div>
              </div>

              {section.fields.map((field) => (
                <div key={field} className="mb-4">
                  <label className="block text-sm font-medium text-[#6B5C57] mb-2">
                    {field.charAt(0).toUpperCase() + field.slice(1)}
                  </label>
                  {editingSection === section.id ? (
                    <textarea
                      value={editedContent[field] || content[section.id]?.[field] || ''}
                      onChange={(e) => setEditedContent({ ...editedContent, [field]: e.target.value })}
                      className="w-full px-4 py-2 rounded-xl border border-[#D4B4A8] focus:outline-none focus:ring-2 focus:ring-[#4A3B36] min-h-[100px]"
                    />
                  ) : (
                    <p className="text-[#4A3B36] bg-[#F8F3F0] p-4 rounded-xl">
                      {content[section.id]?.[field] || defaultContent[section.id][field] || ''}
                    </p>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
